import { AgGridReact } from '@mint-lib/ag-grid/react';
import { useCallback } from 'react';

import type { NewValueParams } from '@mint-lib/ag-grid/community';
import type { AxiosError } from '@mint-lib/api';
import { useTranslation, type TFunction } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import type { DateRange } from '@mint-lib/tw-components/patterns';
import type { MediaPlan } from '@mint-sdk/api-media-plan';
import { isEqual } from 'date-fns';

import { useCreateFlightsApi } from '../api/mutations/useCreateFlight.js';
import { useDeleteFlightsApi } from '../api/mutations/useDeleteFlight.js';
import { useUpdateFlightsApi } from '../api/mutations/useUpdateFlights.js';
import useFlightsApi from '../api/queries/useFlightsApi.js';
import type { FlightItem } from '../types/flights.types.js';
import { updateErrorInContext, type ErrorData } from '../utils/grid.utils.js';

type Props = {
  flightsGridRef: React.RefObject<AgGridReact<FlightItem> | null>;
  activityId: string;
  onFlightCreated?: () => void;
};

export const useFlights = ({
  flightsGridRef,
  activityId,
  onFlightCreated,
}: Props) => {
  const { t } = useTranslation('@myn/planning_v2');

  const { data, isLoading } = useFlightsApi(activityId);

  const { mutateAsync: createFlights } = useCreateFlightsApi();
  const { mutateAsync: deleteFlights } = useDeleteFlightsApi();
  const { mutateAsync: updateFlights } = useUpdateFlightsApi();

  const handleError = (err: AxiosError<MediaPlan.BaseResponseSchema>) => {
    const message = err?.response?.data?.error?.message;

    if (!message) {
      return;
    }

    enqueueSnackbar({
      title: 'Error',
      subTitle: message,
      variant: 'error',
    });
  };

  const handleCreateFlights = async () => {
    try {
      await createFlights({
        activityId,
        type: 'custom',
      });

      onFlightCreated?.();
    } catch (err) {
      handleError(err);
    }
  };

  const handleDeleteLastFlights = async () => {
    const lastItem = data?.items.at(-1);

    if (!lastItem) {
      return;
    }

    try {
      await deleteFlights({
        flightIds: [lastItem.id],
        activityId,
      });
    } catch (err) {
      handleError(err);
    }
  };

  const callFlightUpdate = useCallback(
    async (flightId: string, data: MediaPlan.UpdateFlightRequest) => {
      try {
        await updateFlights({
          flightId,
          activityId,
          data,
        });
      } catch (err) {
        handleError(err);
      }
    },
    [updateFlights, activityId],
  );

  const handleDateCellUpdate = useCallback(
    async (params: NewValueParams<FlightItem, DateRange>) => {
      const { oldValue, newValue } = params;

      if (
        !newValue?.from ||
        !newValue?.to ||
        !oldValue?.from ||
        !oldValue?.to
      ) {
        return;
      }

      if (
        isEqual(newValue.from, oldValue.from) &&
        isEqual(newValue.to, oldValue.to)
      ) {
        return;
      }

      const flightId = params.data.id;

      await callFlightUpdate(flightId, {
        dateFrom: params.data.dateFrom,
        dateTo: params.data.dateTo,
      });
    },
    [callFlightUpdate],
  );

  const handleCellUpdate = useCallback(
    async (params: NewValueParams<FlightItem, number | string>) => {
      const name = params.colDef.field ?? params.colDef.colId;

      if (!name) {
        return;
      }

      const value = params.newRawValue?.toString() ?? '';
      const flightId = params.data.id;

      const errorData = validateCell(name, value, t);

      updateErrorInContext({
        rowId: flightId,
        colId: name,
        errorData,
        gridApi: flightsGridRef.current?.api ?? null,
      });

      flightsGridRef.current?.api.refreshCells();

      if (errorData) {
        return;
      }

      callFlightUpdate(flightId, { [name]: value });
    },
    [callFlightUpdate, flightsGridRef, t],
  );

  return {
    data,
    isLoading,
    handleCreateFlights,
    handleDeleteLastFlights,
    handleCellUpdate,
    handleDateCellUpdate,
  };
};

function validateCell(
  colId: string,
  value: string,
  t: TFunction,
): ErrorData | null {
  if (colId === 'budget') {
    if (value === '') {
      return {
        message: t('Budget is required'),
        errorValue: '',
      };
    }
  }

  return null;
}
